import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../../types/state/AppState';
import { SubscriptionFeature } from '../../../../types/billing/SubscriptionFeature';
import { AnalyticsWidget } from '../../../amrPipeline/common/analytics/AnalyticsWidget';
import { BankAnalyticsFilter } from '../../../../types/analytics/AnalyticsFilter';
import { ChartRequiredFeature } from '../../../access/ChartRequiredFeature';
import { BaseAnalyticsComponentProps, GetAction, withAnalyticsRequest } from '../../../common/manager-analytics/withAnalyticsRequest';
import { ClientsActivityChart } from '../../../common/manager-analytics/ClientsActivityChart';
import { banksActions } from '../../../../actions/banks.actions';
import { BankAccessType } from '../../../../types/amr-pipeline/enums/BankAccessType';

const selector = (state: AppState) => state.banks.analytics;

const getAction = (companyReferenceName: string, filter: BankAnalyticsFilter) => {
    return banksActions.clientsActivityRequest(
        companyReferenceName,
        filter.date.dateFrom || undefined,
        filter.date.dateTo || undefined,
    );
};

function ClientsActivityWidgetComponent({ companyReferenceName, isLoaded, users, accessTypes }: BaseAnalyticsComponentProps<BankAccessType>) {
    const dispatch = useDispatch();
    const state = useSelector(selector);

    const { clientsActivity } = state;
    const { filter, data } = clientsActivity;

    const handleFilterChange = useCallback((filter: BankAnalyticsFilter) => {
        dispatch(banksActions.clientsActivityFilterChange(filter));
    }, [dispatch]);

    const handleBarClick = useCallback((accessType: BankAccessType, date: Date) => {
        dispatch(
            banksActions.profileViewHistoryFilterByBar(
                companyReferenceName,
                accessType,
                date
            )
        );
    }, [dispatch, companyReferenceName]);

    const filteredClientsActivity = useMemo(() => data
        .filter(row => {
            if (filter.users.length && !filter.users.includes(row.userEmail)) {
                return false;
            }

            if (filter.tabs.length && !filter.tabs.some(x => x === row.accessType)) {
                return false;
            }

            return true;
        }), [data, filter.users, filter.tabs]);

    const chart = useMemo(() => <ClientsActivityChart
        onBarClick={handleBarClick}
        filter={filter}
        data={filteredClientsActivity}
        accessTypes={accessTypes}
    // eslint-disable-next-line react-hooks/exhaustive-deps
    />, [handleBarClick, filter, filteredClientsActivity]);

    return (
        <ChartRequiredFeature
            feature={SubscriptionFeature.DealerProfileAnalytics}
            chartName="Clients Activity"
            blockedClassName="restricted-placeholder-clients-activity"
        >
            <AnalyticsWidget
                title="Clients Activity"
                users={users}
                isLoading={isLoaded}
                onFilterChange={handleFilterChange}
                state={clientsActivity}
                tabDefinitions={accessTypes}
                feature={SubscriptionFeature.DealerProfileAnalytics}
            >
                {chart}
            </AnalyticsWidget >
        </ChartRequiredFeature>
    );
}

export const ClientsActivityWidget = withAnalyticsRequest(
    getAction as GetAction,
    selector,
    (state) => state.clientsActivity
)(ClientsActivityWidgetComponent);
